import dayjs from "dayjs";
import { Component, signal, OnInit, inject, input, output } from "@angular/core";
import {
  createFileAndDownload,
  extractErrorMessage,
} from "../../../../core-ui/services/utils";
import {
  IOpenAITranslationMessage,
  ITranscriptTransformStatus,
  ITransformContent,
} from "../../../../core-ui/models/interfaces";
import { NotificationService } from "../../../../core-ui/services/notification.service";
import {
  TranscriptStateEnum,
  TranscriptTransformType,
} from "../../../../core-ui/core-ui.enums";
import { TranscriptService } from "../../../../core-ui/services/transcript.service";
@Component({
  selector: "app-composition-transcript-transform",
  templateUrl: "./composition-transcript-transform.component.html",
  styleUrls: ["./composition-transcript-transform.component.scss"],
  standalone: false,
})
export class CompositionTranscriptTransformComponent implements OnInit {
  private transcript = inject(TranscriptService);
  private notification = inject(NotificationService);

  readonly conversationId = input<string>(undefined);
  readonly transcriptId = input<string>(undefined);
  readonly transform = input<ITranscriptTransformStatus>(undefined);
  readonly transformType = input<TranscriptTransformType>(undefined);

  readonly removed = output<void>();

  url = signal<string>(null);
  confirmRemove = signal(false);
  title: string;
  subtitle: string;
  icon: string;
  isLoadingURL = signal(false);
  isLoadingContent = signal(false);
  isRemoveLoading = signal(false);
  isContentExpanded = signal(false);
  canRequestFormattedTranscript = false;
  isTranslate = false;
  translateData = signal<IOpenAITranslationMessage[]>([]);
  transformData = signal<ITransformContent>(undefined);
  copyData = signal(undefined);

  ngOnInit(): void {
    const transformType = this.transformType();
    switch (transformType) {
      case "sentiment":
        this.icon = "file-sentiment";
        this.title = "Sentiment Analysis";
        this.subtitle = "Positive, negative or neutral";
        break;
      case "summary":
        this.icon = "file-summary";
        this.title = "Summary";
        this.subtitle = "Basic bullet points of the conversation";
        break;
      case "translation":
        this.icon = "file-translation";
        this.title = "Translation";
        this.subtitle = "English";
        break;
      case "prompt":
        this.icon = "file-prompt";
        this.title = "Custom";
        this.subtitle = "Custom prompt";
    }
    this.canRequestFormattedTranscript =
      this.transcript.isDownloadFormattedTranscriptSupported(
        transformType
      ) && !this.transcript.isLegacyGoogleProvider;
    this.isTranslate = transformType === "translation";
  }

  async getURL() {
    try {
      this.isLoadingURL.set(true);
      const data =
        this.transformType() === "translation"
          ? await this.transcript.getTranslationURL(
              this.conversationId(),
              this.transcriptId(),
              this.transform().id
            )
          : await this.transcript.getTransformURL(
              this.conversationId(),
              this.transcriptId(),
              this.transform().id
            );
      this.url.set(data.url);
    } catch (ex) {
      this.notification.error("Could not retrieve URL", {
        body: extractErrorMessage(ex),
      });
    } finally {
      this.isLoadingURL.set(false);
    }
  }

  async removeTransform() {
    try {
      this.isRemoveLoading.set(true);
      this.confirmRemove.set(false);
      this.transformType() === "translation"
        ? await this.transcript.removeTranslation(
            this.conversationId(),
            this.transcriptId(),
            this.transform().id
          )
        : await this.transcript.removeTransform(
            this.conversationId(),
            this.transcriptId(),
            this.transform().id
          );
      this.transformData.set(undefined);
      this.translateData.set([]);
      // TODO: The 'emit' function requires a mandatory void argument
      this.removed.emit();
    } catch (ex) {
      this.notification.error("Could not remove file", {
        body: extractErrorMessage(ex),
      });
    } finally {
      this.isRemoveLoading.set(false);
    }
  }

  async toggleContent() {
    if (this.isLoadingContent()) return;
    if (this.isContentExpanded()) {
      this.isContentExpanded.set(false);
    } else {
      if (
        (this.isTranslate && this.translateData().length === 0) ||
        (!this.isTranslate && !this.transformData())
      ) {
        await this.getContent();
      }
      this.isContentExpanded.set(true);
    }
  }

  async getContent(): Promise<IOpenAITranslationMessage[] | ITransformContent> {
    try {
      if (this.transformType() === "translation") {
        if (this.translateData().length > 0) return this.translateData();
        //
        this.isLoadingContent.set(true);
        const resp = (await this.transcript.getTranslationContent(
          this.conversationId(),
          this.transcriptId(),
          this.transform().id
        )) as IOpenAITranslationMessage[];
        this.translateData.set(resp);
        this.copyData.set(JSON.stringify(resp));
        return this.translateData();
      } else {
        if (this.transformData()) return this.transformData();
        //
        this.isLoadingContent.set(true);
        const resp = await this.transcript.getTransformContent(
          this.conversationId(),
          this.transcriptId(),
          this.transform().id
        );
        this.transformData.set(resp);
        this.copyData.set(resp.result);
        return this.transformData();
      }
    } catch (ex) {
      this.notification.error("Could not retrieve content", {
        body: extractErrorMessage(ex),
      });
      throw ex;
    } finally {
      this.isLoadingContent.set(false);
    }
  }

  async download() {
    const response = await this.getContent();
    createFileAndDownload(
      JSON.stringify(response),
      `${this.transformType()}.json`,
      "text/plain"
    );
  }

  get isCompleted() {
    return (
      this.transform().state.toLowerCase() ===
      TranscriptStateEnum.completed.toLowerCase()
    );
  }

  get isProcessing() {
    return (
      this.transform().state.toLowerCase() ===
      TranscriptStateEnum.processing.toLowerCase()
    );
  }
}
